/**
 * Universal export utilities composable
 * Reusable from anywhere with any data structure
 */

import ExportJson from '@/services/ExportJsonService';
import { formatTime } from '@/services/utils';
import { parse, format, isValid } from 'date-fns';
export function useExportUtilities() {
  /**
   * Clean any field value for CSV export
   */
  const cleanField = value => {
    if (!value) return '';
    if (typeof value === 'object' && value.name) return value.name;
    return String(value).replace(/[\r\n]+/g, ' ');
  };

  /**
   * Format date for CSV export with intelligent Excel/text editor compatibility
   * Extracts the YYYY-MM-DD portion from any date string and treats it as a
   * calendar date (local time), not a moment in time.
   *
   * Why: new Date("YYYY-MM-DD") and new Date("YYYY-MM-DDT05:00:00Z") both
   * can shift back a day in US timezones due to UTC-to-local conversion.
   * Since all our date values represent calendar dates (entry date, expense
   * date, subscription periodEnd), we extract the date portion and parse
   * with date-fns parse() which treats it as local midnight.
   *
   * See: story #42644 (table display fix), story #43417 (export fix)
   */
  const formatDate = date => {
    if (!date) return '';
    let dateObj;
    if (typeof date === 'string') {
      // Extract YYYY-MM-DD from any ISO string (bare date, datetime, or with timezone)
      // e.g. "2026-02-13", "2026-02-13T08:54:00Z", "2026-09-15T05:04:48+00:00"
      const dateMatch = date.match(/^(\d{4}-\d{2}-\d{2})/);
      if (dateMatch) {
        // parse() treats as local time, avoiding UTC midnight shift
        dateObj = parse(dateMatch[1], 'yyyy-MM-dd', new Date());
      } else {
        dateObj = new Date(date);
      }
    } else {
      dateObj = new Date(date);
    }
    if (!isValid(dateObj)) return '';
    return format(dateObj, 'yyyy-MM-dd');
  };

  /**
   * Universal CSV export - works with any data and column mapping
   * @param {Array} data - Array of objects to export
   * @param {Object} columnMap - Maps CSV headers to data paths/transforms
   * @param {Object} options - Export options
   * 
   * Example usage:
   * exportCSV(timeEntries, {
   *   'Date': (item) => formatDate(item.date),
   *   'Client': 'client_name',
   *   'Hours': 'hours'
   * }, { filename: 'time-entries' })
   */
  const exportCSV = async (data, columnMap, options = {}) => {
    if (!data?.length) return;
    const csvData = data.map(item => {
      const row = {};
      Object.entries(columnMap).forEach(([header, mapping]) => {
        if (typeof mapping === 'function') {
          // Custom transform function
          row[header] = mapping(item);
        } else if (typeof mapping === 'string') {
          // Simple property path
          row[header] = cleanField(item[mapping]);
        } else if (mapping?.condition) {
          // Conditional field
          row[header] = mapping.condition(item) ? mapping.value(item) : mapping.default || '';
        } else {
          row[header] = cleanField(item[mapping]);
        }
      });
      return row;
    });
    const filename = options.filename || 'export';
    const exportJson = new ExportJson();
    exportJson.downloadCsv(csvData, filename);
  };

  /**
   * Quick export for time entries - maintains backward compatibility
   * Supports both old and new calling patterns
   */
  const exportTimeEntries = async (entries, mattersOrOptions = {}, exportOptions = {}, filter = {}) => {
    let options = {};
    if (Array.isArray(mattersOrOptions)) {
      options = {
        ...exportOptions,
        filter
      };
    } else {
      options = mattersOrOptions;
    }
    const columnMap = {
      'Date': entry => formatDate(entry.originalDate || entry.date),
      'Timekeeper': entry => cleanField(entry.timekeeper_initials || entry.timekeeper),
      'Client Id': 'client_Id',
      'Client': 'client',
      'Matter Id': 'matter_reference',
      'Matter': 'matter',
      'Description': 'description',
      'Hours': 'hours',
      ...(options.includeStartEndTimes && {
        'Start Time': entry => entry.originalStartTime ? formatTime(entry.originalStartTime) : '',
        'Stop Time': entry => entry.originalEndTime ? formatTime(entry.originalEndTime) : ''
      }),
      ...(options.includeRates && {
        'Rate': 'rate',
        'Amount': 'amount'
      }),
      'Billing Type': 'billingType',
      ...(options.includeActivityCodes && {
        'Activity Code': entry => entry.codes?.activity?.code || ''
      }),
      ...(options.includeTaskCodes && {
        'Task Code': entry => entry.codes?.task?.code || ''
      }),
      ...(options.includeFixedFee && {
        'Fixed Fee': entry => entry.charge ? entry.charge.label || entry.charge.description || '' : entry.billingType === 'fixedFee' ? 'Unassigned' : ''
      }),
      ...(options.customFlag && {
        [options.customFlag]: entry => entry.customFlag ? 'Yes' : 'No'
      }),
      ...(options.includeNotes && {
        'Notes': 'note'
      })
    };
    const filename = `leanlaw-export-${options.filter?.range?.label || 'entries'}`.toLowerCase().replace(/\s+/g, '-');
    await exportCSV(entries, columnMap, {
      filename
    });
  };

  /**
   * Quick export for expenses - maintains backward compatibility  
   * Supports both old and new calling patterns
   */
  const exportExpenses = async (expenses, formatCurrencyOrOptions, exportOptions = {}, filter = {}) => {
    let formatCurrency = null;
    let options = {};
    if (typeof formatCurrencyOrOptions === 'function') {
      formatCurrency = formatCurrencyOrOptions;
      options = {
        ...exportOptions,
        filter
      };
    } else {
      options = formatCurrencyOrOptions || {};
      formatCurrency = options.formatCurrency;
    }
    const columnMap = {
      'Date': expense => formatDate(expense.date),
      'Client Id': expense => cleanField(expense.client?.reference),
      'Client': expense => cleanField(expense.client?.label),
      'Matter Id': expense => cleanField(expense.matter?.reference),
      'Matter': expense => cleanField(expense.matter?.label),
      'Description': 'description',
      'Amount': expense => formatCurrency ? formatCurrency(expense.amount) : expense.amount,
      ...(options.includeVendor && {
        'Vendor': 'vendor'
      }),
      'User': expense => cleanField(expense.account?.initials || expense.account?.name),
      'Billed': expense => expense.billed ? 'Yes' : 'No',
      ...(options.includeCheckNumber && {
        'Check Number': expense => cleanField(expense.checkNumber)
      }),
      ...(options.customFlag && {
        [options.customFlag]: expense => expense.customFlag ? 'Yes' : 'No'
      })
    };
    const filename = `leanlaw-export-${options.filter?.range?.label || 'expenses'}`.toLowerCase().replace(/\s+/g, '-');
    await exportCSV(expenses, columnMap, {
      filename
    });
  };
  return {
    // Universal functions - use these from anywhere
    exportCSV,
    formatDate,
    cleanField,
    // Convenience functions for common use cases
    exportTimeEntries,
    exportExpenses
  };
}